import styled from 'styled-components/macro'
import BootstrapNav from 'react-bootstrap/Nav'
import BootstrapContainer from 'react-bootstrap/Container'
import BootstrapRow from 'react-bootstrap/Row'
import BootstrapTab from 'react-bootstrap/Tab'
import { grid, typography } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'
import { styled as muiStyled } from '@mui/material/styles'
import {
  Box,
  Card,
  Divider,
  List,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material'
import palette from 'src/designSystem/variables/palette'

export const FileCaption = muiStyled(Typography)<TypographyProps>(() => ({
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
}))

export const TabContainer = styled(BootstrapContainer)
  .withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) =>
      defaultValidatorFn(prop) || prop === 'fluid',
  })
  .attrs({ fluid: true })`
  grid-column: 1 / -1;
  ${grid}
  margin: 0;
  padding: 0;
`

export const TabRow = styled(BootstrapRow)`
  margin: 0;
`

export const TabNav = styled(BootstrapNav).attrs({
  variant: 'tabs',
})`
  flex-grow: 1;
  padding-left: 12px;
  border-bottom: 1px solid #acb3c3;
  margin-bottom: 12px;
`

export const TabTitle = styled(BootstrapNav.Link)`
  &.nav-link {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0;
    padding-left: ${themeGet('space.m')}px;
    padding-right: ${themeGet('space.m')}px;
    border: none;
    border-bottom: 4px solid transparent;
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
    color: ${themeGet('colors.text.med')};

    ${themeGet('textStyles.subtitle.1')}
    :hover {
      background-color: ${themeGet('colors.bg.primary')};
      border-bottom: 4px solid ${themeGet('colors.border.primary.base')};
    }

    &.active {
      border: none;
      border-bottom: 4px solid ${themeGet('colors.border.primary.dark')};
      color: ${themeGet('colors.text.default')};
      font-weight: ${themeGet('fontWeights.semiBold')};
    }

    .tab-title {
      ${typography}
    }
  }
`

export const TabContent = styled(BootstrapTab.Content)`
  flex-grow: 1;
  margin: 0;
  padding: 0;
`

export const TabNavItem = styled(TabNav.Item)`
  cursor: pointer;
`

export { BootstrapTab }

// Layout primitives for Confluence picker panes.

export const BrowserRoot = muiStyled(Stack)(() => ({
  height: 560,
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
}))

export const TREE_PANE_WIDTH = 300

export const TreePaneContainer = muiStyled(Box)(({ theme }) => ({
  width: TREE_PANE_WIDTH,
  flexShrink: 0,
  overflowX: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflowY: 'auto',
}))

export const TreePaneList = muiStyled(List)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}))

export const RightPane = muiStyled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
}))

export const RightPaneHeader = muiStyled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
}))

export const RightPaneDivider = muiStyled(Divider)(() => ({}))

export const SearchContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  height: 32px;
  padding-left: 12px;
  padding-right: 12px;

  border-radius: 20px;
  background-color: white;
  border: 1px solid ${palette.neutral['100']};
  overflow: visible;
`

export const SearchInput = muiStyled('input')(() => ({
  border: 'none',
  outline: 'none',
  fontSize: 14,
  fontWeight: 400,
  color: 'inherit',
  flexGrow: 1,
  height: 32,
  padding: '0 10px',
  minWidth: 0,
  background: 'transparent',
}))

export const SuggestionsPopover = muiStyled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 10,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  maxHeight: 280,
  overflowY: 'auto',
}))

// One size across the header row: the root caption, the path and the actions.
export const HEADER_FONT_SIZE = '0.75rem'

export const BreadcrumbsSx = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  fontSize: HEADER_FONT_SIZE,
  '& .MuiBreadcrumbs-ol': {
    // Bound the list to the space left by the caption and actions so the
    // right-aligned current folder stays visible instead of being clipped.
    minWidth: 0,
    maxWidth: '100%',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
  },
  '& .MuiBreadcrumbs-li': {
    minWidth: 0,
  },
  '& .MuiBreadcrumbs-separator': {
    fontSize: HEADER_FONT_SIZE,
    mx: 0.5,
  },
}

export const BreadcrumbLinkSx = {
  fontSize: HEADER_FONT_SIZE,
  lineHeight: 1.2,
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const BreadcrumbCurrentSx = {
  fontSize: HEADER_FONT_SIZE,
  fontWeight: 700,
  color: 'text.primary',
  lineHeight: 1.2,
  maxWidth: 280,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const RightPaneLoading = muiStyled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: 48,
  paddingBottom: 48,
}))

export const RightPaneFooter = muiStyled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  display: 'flex',
  justifyContent: 'flex-end',
}))

export const FooterActions = muiStyled(Stack)(() => ({
  flexShrink: 0,
  '& .MuiButton-root, & .MuiChip-label, & .MuiTypography-root': {
    fontSize: HEADER_FONT_SIZE,
  },
}))

/**
 * Grid + table view primitives (to avoid scattered `sx`).
 */

export const GridPaneContainer = muiStyled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  minHeight: 0,
  overflow: 'auto',
}))

export const EmptyState = muiStyled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}))

export const GridCard = muiStyled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
}))

export const GridTitle = muiStyled(Typography)(() => ({
  fontWeight: 500,
  maxWidth: '100%',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}))

export const GridCheckboxWrap = muiStyled(Box)(() => ({
  marginLeft: 'auto',
}))

export const GridMetaRow = muiStyled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(1),
}))

export const TablePaneContainer = muiStyled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',

  // If your OS/browser uses overlay scrollbars, this prevents the last column
  // from sitting underneath the scrollbar.
  paddingRight: theme.spacing(1),
}))

/**
 * Results table
 *
 * Notes:
 * - Use fixed layout for predictable column sizing.
 * - Size utility columns in px; let the Name column take the remainder.
 * - Allow the Name cell to wrap so long titles aren't clipped.
 */

const kColCheckboxPx = 44
const kColTypePx = 140
const kColModifiedPx = 180

export const ResultsTable = muiStyled('table')(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  boxSizing: 'border-box',
}))

export const TableHeadRow = muiStyled('tr')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
}))

export const Th = muiStyled('th')(({ theme }) => ({
  textAlign: 'left',
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  boxSizing: 'border-box',
}))

export const ThCheckbox = muiStyled(Th)(() => ({
  width: kColCheckboxPx,
  textAlign: 'center',
  paddingLeft: 0,
  paddingRight: 0,
}))

export const ThType = muiStyled(Th)(() => ({
  width: kColTypePx,
}))

export const ThModified = muiStyled(Th)(() => ({
  width: kColModifiedPx,
}))

export const Td = muiStyled('td')(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  verticalAlign: 'middle',
  boxSizing: 'border-box',

  // Default cell behavior: do not overflow its column.
  overflow: 'hidden',
}))

export const TdCheckbox = muiStyled(Td)(() => ({
  width: kColCheckboxPx,
  textAlign: 'center',
  paddingLeft: 0,
  paddingRight: 0,
}))

export const TdType = muiStyled(Td)(() => ({
  width: kColTypePx,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}))

export const TdModified = muiStyled(Td)(() => ({
  width: kColModifiedPx,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}))

// Primary/title cell: allow wrapping so long names don't get clipped.
export const TdName = muiStyled(Td)(() => ({
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}))

export const Tr = muiStyled('tr')(({ theme }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

export const TrSelected = muiStyled(Tr)(({ theme }) => ({
  backgroundColor: theme.palette.action.selected,
}))
